import { api } from '@/lib/api-client'
import { HIDDEN_ENTITY_IDS } from '@/shared/constants/demo-data'

import type { Entity, EntityStats } from '../types/entity.types'

// ── Types ──────────────────────────────────────────────────────────────────

interface ApiDashboardStats {
  total_entities: number
  total_sites: number
  surveys_scheduled: number
  surveys_completed: number
  surveys_missed: number
  issues_active: number
  survey_progress_percent: number
  period: { month: string; date_from: string; date_to: string }
  series?: {
    bucket_size: string
    buckets: string[]
    metrics: Record<string, ReadonlyArray<number | null>>
  }
}

// Snapshot delta — absolute change in the running value this month.
// `series[0]` is the value at month-start; `current` is now. The card
// surfaces this as "+N this month" rather than a percentage because
// percentages off a zero baseline (fresh-seeded stage, freshly onboarded
// entities) read as misleading "+100%" growth.
function snapshotDelta(
  series: ReadonlyArray<number | null> | undefined,
  current: number,
): number {
  if (!series || series.length === 0) {return 0}
  const first = series[0] ?? 0
  return current - first
}

// Counter delta — total activity this month. Backend ships per-bucket
// counts; summing gives the month-to-date total. Without a previous-month
// baseline (backend doesn't ship one on this endpoint yet), this IS the
// most honest "what happened this month" number.
function counterMonthTotal(series: ReadonlyArray<number | null> | undefined): number {
  if (!series) {return 0}
  let sum = 0
  for (const v of series) {
    if (v !== null) {sum += v}
  }
  return sum
}

interface ApiEntity {
  id: string
  name: string
  description: string | null
  short_code?: string | null
  is_active: boolean
  status: string
  survey_status: string
  surveys_scheduled: number
  issues_active: number
  questions_count: number
  sites_count: number
  created_on: string
  updated_on: string
}

interface ApiEntitiesResponse {
  entities: ApiEntity[]
  total_count: number
  survey_not_initiated_count: number
}

export interface CreateEntityRequest {
  name: string
  description?: string
}

// ── API functions ───────────────────────────────────────────────────────────

export async function getEntityStats(): Promise<EntityStats> {
  // The KPI strip relies on the server stats directly. The backend now
  // excludes blocked/hidden entities from this aggregation, so there is no
  // client-side netting — display the API counts verbatim. Deltas come from
  // the per-bucket `series` the same endpoint ships.
  const d = await api.get<ApiDashboardStats>('/dashboard/entities/stats')
  const m = d.series?.metrics
  return {
    totalEntities: d.total_entities,
    totalEntitiesDelta: snapshotDelta(m?.['total_entities'], d.total_entities),
    totalSites: d.total_sites,
    totalSitesDelta: snapshotDelta(m?.['total_sites'], d.total_sites),
    surveysScheduled: d.surveys_scheduled,
    surveysScheduledDelta: counterMonthTotal(m?.['surveys_scheduled']),
    surveysCompleted: d.surveys_completed,
    surveysCompletedDelta: counterMonthTotal(m?.['surveys_completed']),
    surveysMissed: d.surveys_missed,
    surveysMissedDelta: counterMonthTotal(m?.['surveys_missed']),
    issuesActive: d.issues_active,
    issuesActiveDelta: snapshotDelta(m?.['issues_active'], d.issues_active),
    surveyProgress: d.survey_progress_percent,
  }
}

const CARD_IMAGES = [
  '/image/entities_card_1.svg',
  '/image/entities_card_2.svg',
  '/image/entities_card_3.svg',
]

export interface EntitiesResult {
  entities: Entity[]
  totalCount: number
  surveyNotInitiatedCount: number
}

export async function getEntities(): Promise<EntitiesResult> {
  const d = await api.get<ApiEntitiesResponse>('/entities')
  const statusMap: Record<string, Entity['status']> = {
    ongoing: 'ongoing',
    survey_not_initiated: 'survey_not_initiated',
    completed: 'completed',
  }
  const visible = d.entities.filter((e) => !HIDDEN_ENTITY_IDS.has(e.id))
  return {
    totalCount: visible.length,
    surveyNotInitiatedCount: visible.filter((e) => e.survey_status === 'survey_not_initiated').length,
    entities: visible.map((e, i) => ({
      id: e.id,
      name: e.name,
      status: (statusMap[e.survey_status] ?? 'ongoing') as Entity['status'],
      isActive: e.is_active,
      progress: 0,
      completed: 0,
      missed: 0,
      pending: 0,
      totalSites: e.sites_count,
      surveysScheduled: e.surveys_scheduled,
      issuesActive: e.issues_active,
      image: CARD_IMAGES[i % CARD_IMAGES.length],
      description: e.description,
      shortCode: e.short_code ?? null,
    })),
  }
}

export function createEntity(data: CreateEntityRequest): Promise<{ id: string; name: string }> {
  return api.post('/entities', data)
}
